/**
 * Sellable bundles for the sponsor wizard. Each package pre-selects a set
 * of placements, a flight length and a suggested contract price so an
 * operator can go from "the client said yes" to a filled wizard in one
 * click. Prices follow the launch rate card (pin EC$150 a month, sheet
 * campaign EC$400 per two weeks); they are suggestions, never enforced.
 */

import type { CampaignTypeKey } from '@/stores/campaignWizardStore';

export type CampaignPackageKey =
  | 'festival_partner'
  | 'map_starter'
  | 'sheet_campaign'
  | 'trip_partner'
  | 'island_takeover';

export interface CampaignPackage {
  key: CampaignPackageKey;
  name: string;
  pitch: string;
  /** First entry drives the live preview and creative defaults. */
  campaignTypes: CampaignTypeKey[];
  days: number;
  priceXcd: number;
  goodFor: string[];
  highlight?: boolean;
}

export const CAMPAIGN_PACKAGES: CampaignPackage[] = [
  {
    key: 'map_starter',
    name: 'Map Starter',
    pitch: 'Branded pin with their name on the map, plus a "Take a bus there" button.',
    campaignTypes: ['sponsored_map_pin'],
    days: 30,
    priceXcd: 150,
    goodFor: ['Shops', 'Restaurants', 'Salons'],
  },
  {
    key: 'sheet_campaign',
    name: 'Sheet Campaign',
    pitch: 'Offer cards in the Where to and Confirm ride sheets, shown to riders nearby.',
    campaignTypes: ['passenger_sheet_idle', 'passenger_sheet_confirm'],
    days: 14,
    priceXcd: 400,
    goodFor: ['Promotions', 'Grocery', 'Telecoms'],
  },
  {
    key: 'trip_partner',
    name: 'Trip Partner',
    pitch: 'Pin on the map and offer cards through the whole trip, from search to on the bus.',
    campaignTypes: [
      'passenger_sheet_idle',
      'passenger_sheet_confirm',
      'passenger_sheet_active_ride',
      'sponsored_map_pin',
    ],
    days: 30,
    priceXcd: 900,
    goodFor: ['Retail chains', 'Restaurants', 'Pharmacies'],
    highlight: true,
  },
  {
    key: 'island_takeover',
    name: 'Island Takeover',
    pitch: 'Top banner on the home screen, every rider sheet, the ride-complete screen and a pin.',
    campaignTypes: [
      'passenger_home_banner',
      'passenger_sheet_idle',
      'passenger_sheet_confirm',
      'passenger_sheet_active_ride',
      'ride_complete_takeover',
      'sponsored_map_pin',
    ],
    days: 30,
    priceXcd: 1500,
    goodFor: ['Banks', 'Telecoms', 'Hotels'],
  },
  {
    key: 'festival_partner',
    name: 'Festival Presenting Partner',
    pitch: 'Full-screen splash when the app opens, the top banner and Where to cards for festival week.',
    campaignTypes: ['launch_splash', 'passenger_home_banner', 'passenger_sheet_idle'],
    days: 7,
    priceXcd: 3500,
    goodFor: ['Discover Dominica', 'Event promoters', 'Telecoms'],
  },
];

export function getCampaignPackage(key: string | null | undefined): CampaignPackage | undefined {
  return CAMPAIGN_PACKAGES.find((p) => p.key === key);
}

/** Package price scaled to a different flight length, rounded to EC$10. */
export function suggestedPackagePrice(pkg: CampaignPackage, days: number): number {
  if (days <= 0) return 0;
  if (days === pkg.days) return pkg.priceXcd;
  return Math.max(10, Math.round((pkg.priceXcd * days) / pkg.days / 10) * 10);
}

/**
 * Splits one deal's contract amount across the campaign rows it publishes
 * so reports that sum `invoice_amount_xcd` add back up to the deal total.
 * Works in cents; any leftover cent goes to the first row.
 */
export function splitContractAmount(total: number, parts: number): number[] {
  if (parts <= 0) return [];
  if (!Number.isFinite(total) || total <= 0) return Array(parts).fill(0);
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / parts);
  const remainder = cents - base * parts;
  return Array.from({ length: parts }, (_, i) => (base + (i === 0 ? remainder : 0)) / 100);
}
